import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { resolveClientHome } from '@/lib/auth/clientHome';

interface PublicRouteProps {
  children: React.ReactNode;
}

function hasStoredToken(): boolean {
  try {
    return !!(localStorage.getItem('accessToken') || localStorage.getItem('token'));
  } catch {
    return false;
  }
}

export function PublicRoute({ children }: PublicRouteProps) {
  // A login link replaces whatever session is stored: the page validates it itself, so don't check (or flash a spinner for) the old one
  const [hasLoginLink] = useState(() => new URLSearchParams(window.location.search).has('token'));
  const [isChecking, setIsChecking] = useState(() => !hasLoginLink && hasStoredToken());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [clientHome, setClientHome] = useState<string | null>(null);

  useEffect(() => {
    if (isChecking) checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

    if (!token) {
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }

    try {
      // Call the backend to verify token and get user role
      const response = await apiClient.get('/auth/me');
      const user = response.data.user;

      if (user.role !== 'super_admin' && user.role !== 'manager_admin') {
        setClientHome(await resolveClientHome());
      }
      setIsAuthenticated(true);
      setUserRole(user.role);
    } catch (error) {
      // Token is invalid or expired
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      setUserRole(null);

      // Clear invalid tokens
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setIsChecking(false);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
        <div className="text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  // If authenticated, redirect to appropriate dashboard
  if (isAuthenticated) {
    if (userRole === 'super_admin' || userRole === 'manager_admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to={clientHome ?? '/dashboard'} replace />;
  }

  // Not authenticated, show public page
  return <>{children}</>;
}
